/**
 * Health Check API Endpoint for Railway
 *
 * This endpoint is used by Railway to determine if the service is healthy and ready to receive traffic.
 * It performs readiness checks and returns diagnostics information.
 *
 * Railway will poll this endpoint after deployment. The service is considered healthy when this
 * endpoint returns HTTP 200.
 */

import { createFileRoute } from '@tanstack/react-router';

// Server startup time for uptime calculation
const SERVER_START_TIME = Date.now();

// Minimum uptime before considering server "ready" (in milliseconds)
const MIN_READY_TIME = 2000; // 2 seconds

// The GET handler object is kept as a standalone const: @tanstack/react-start
// 1.168.60 dropped `server` from createFileRoute's parameter type while the
// runtime still honours it for API routes (Railway readiness probe). Passing
// the variable bypasses excess-property checking on the literal.
const healthRouteOptions = {
  server: {
    handlers: {
      GET: async () => {
        const now = Date.now();
        const uptime = now - SERVER_START_TIME;
        const uptimeSeconds = Math.floor(uptime / 1000);

        // Check if server has been running long enough to be considered "ready"
        const isReady = uptime >= MIN_READY_TIME;

        // Gather diagnostics
        const diagnostics = {
          status: isReady ? 'healthy' : 'warming_up',
          ready: isReady,
          timestamp: new Date().toISOString(),
          uptime: {
            milliseconds: uptime,
            seconds: uptimeSeconds,
            formatted: formatUptime(uptimeSeconds),
          },
          environment: {
            nodeEnv: process.env.NODE_ENV || 'development',
            port: process.env.PORT || 'unknown',
            hostname: process.env.HOSTNAME || 'unknown',
          },
          memory: {
            heapUsed: `${Math.round(process.memoryUsage().heapUsed / 1024 / 1024)}MB`,
            heapTotal: `${Math.round(process.memoryUsage().heapTotal / 1024 / 1024)}MB`,
            rss: `${Math.round(process.memoryUsage().rss / 1024 / 1024)}MB`,
          },
          process: {
            pid: process.pid,
            version: process.version,
            platform: process.platform,
          },
        };

        // Return 503 if not ready yet, 200 if healthy
        const status = isReady ? 200 : 503;

        return new Response(JSON.stringify(diagnostics), {
          status,
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'X-Health-Status': isReady ? 'healthy' : 'warming_up',
          },
        });
      },
    },
  },
};

export const Route = createFileRoute('/api/health')(
  // SAFETY: 1.168.60 removed `server` from createFileRoute's parameter types
  // while the runtime still serves it for API routes (Railway readiness
  // probe, verified live). Cast until the settled server-route API is
  // available in these packages.
  healthRouteOptions as unknown as Parameters<typeof createFileRoute<'/api/health'>>[0]
);

/**
 * Format uptime in human-readable format
 */
function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);

  return parts.join(' ');
}
